/**
 * Read/field-scoped write access to the auth-letter-agent prompt config doc
 * (`configurations/agentic-auth-on-file/{orgId}/payer_prompt`).
 *
 * Writes target individual dotted field paths so concurrent edits to different
 * payers never overwrite each other.
 */
import {
  doc,
  FirestoreError,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { logError } from "../../utils/customLogger";
import { firestoreDB } from "../firebase/firestoreService";
import { FirestoreDocumentReference } from "../firebase/references";

/**
 * Read/field-scoped write access to the auth-letter-agent prompt config doc.
 *
 * @example
 * const config = await AuthLetterAgentConfigService.getRawConfig(orgId);
 * await AuthLetterAgentConfigService.updatePayerFields(orgId, fields);
 */
export class AuthLetterAgentConfigService {
  /**
   * Reads the full config document for an org.
   *
   * A missing doc is the normal "no config yet" state, so it returns `{}`
   * rather than throwing.
   *
   * @param organizationId - Org whose payer_prompt doc is read
   * @return The stored config, or `{}` when the doc is absent
   * @throws {Error} When the Firestore read fails
   */
  static async getRawConfig(
    organizationId: string,
  ): Promise<Record<string, unknown>> {
    if (!organizationId) return {};
    const documentPath =
      FirestoreDocumentReference.agenticAuthOnFilePayerPrompt(organizationId);
    try {
      const snapshot = await getDoc(doc(firestoreDB, documentPath));
      return snapshot.exists()
        ? (snapshot.data() as Record<string, unknown>)
        : {};
    } catch (error) {
      logError(
        error as Error,
        `Error fetching auth letter agent config for org ${organizationId}`,
      );
      throw error;
    }
  }

  /**
   * Updates specific fields of the config document.
   *
   * `fields` keys are dotted paths scoped to a single payer (e.g.
   * `j_code_info.<payer>`). If the doc does not exist yet, it is merge-created
   * (so concurrent writes are not wiped) and then the same field patch is
   * applied — never a full copy of another org's config.
   *
   * @param organizationId - Org whose payer_prompt doc is updated
   * @param fields - Map of dotted field paths to their new values
   * @throws {Error} When organizationId is missing or the write fails
   */
  static async updatePayerFields(
    organizationId: string,
    fields: Record<string, any>,
  ): Promise<void> {
    if (!organizationId) throw new Error("Organization is required.");
    const documentPath =
      FirestoreDocumentReference.agenticAuthOnFilePayerPrompt(organizationId);
    const docRef = doc(firestoreDB, documentPath);
    try {
      await updateDoc(docRef, fields);
    } catch (error) {
      const code = (error as FirestoreError)?.code ?? "";
      if (code.includes("not-found")) {
        // Merge-create so a concurrent write can't be wiped by an empty setDoc.
        try {
          await setDoc(docRef, {}, { merge: true });
          await updateDoc(docRef, fields);
          return;
        } catch (createError) {
          logError(
            createError as Error,
            `Error creating auth letter agent config for org ${organizationId}`,
          );
          throw createError;
        }
      }
      logError(
        error as Error,
        `Error updating auth letter agent config for org ${organizationId}`,
      );
      throw error;
    }
  }

  /**
   * Applies the same field patch to every selected org's payer_prompt doc.
   *
   * Uses {@link updatePayerFields} per org so missing docs are created empty
   * and then patched. Partial failures are collected rather than aborting early.
   *
   * @param organizationIds - Orgs to write the same patch to
   * @param fields - Map of dotted field paths to their new values
   * @return Org IDs that failed, paired with their error messages
   * @throws {Error} When no organization IDs are provided
   */
  static async updatePayerFieldsForOrgs(
    organizationIds: string[],
    fields: Record<string, any>,
  ): Promise<{ orgId: string; message: string }[]> {
    const uniqueIds = Array.from(
      new Set(organizationIds.map((id) => id.trim()).filter(Boolean)),
    );
    if (uniqueIds.length === 0) {
      throw new Error("Select at least one organization.");
    }
    const settled = await Promise.allSettled(
      uniqueIds.map((organizationId) =>
        AuthLetterAgentConfigService.updatePayerFields(organizationId, fields),
      ),
    );
    return settled.flatMap((result, index) => {
      if (result.status === "fulfilled") return [];
      const message =
        result.reason instanceof Error
          ? result.reason.message
          : "Unknown error";
      return [{ orgId: uniqueIds[index], message }];
    });
  }
}
